import React, { useState } from 'react';
import { Play } from 'lucide-react';
import Img from './Img';

/**
 * Click-to-load facade for a Facebook video embed.
 *
 * The raw <iframe> was the single most expensive thing on the homepage: it pulled
 * in three third-party origins (facebook.com, static.xx.fbcdn.net,
 * scontent-*.xx.fbcdn.net) before anyone asked to watch anything, shipped
 * uncacheable images, and registered an unload handler in a subframe that
 * disqualified the whole page from the browser's back/forward cache.
 *
 * So we render a local poster and only mount the iframe once someone actually
 * clicks play — at which point autoplay picks up where the click left off.
 */
export default function LazyFacebookVideo({ src, title, poster, posterAlt, className = '' }) {
  const [playing, setPlaying] = useState(false);

  if (playing) {
    return (
      <iframe
        src={`${src}${src.includes('?') ? '&' : '?'}autoplay=true`}
        title={title}
        className={className}
        style={{ border: 'none', overflow: 'hidden', display: 'block', width: '100%' }}
        height="476"
        scrolling="no"
        allowFullScreen
        allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
      />
    );
  }

  return (
    <button
      type="button"
      onClick={() => setPlaying(true)}
      className={`group relative block w-full overflow-hidden bg-[#111] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C97B2E] ${className}`}
      style={{ height: '476px' }}
    >
      <Img
        src={poster}
        alt={posterAlt}
        className="absolute inset-0 w-full h-full object-cover opacity-80 transition-opacity duration-300 group-hover:opacity-95"
        sizes="300px"
      />
      <span className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-black/30" />
      <span className="absolute inset-0 flex flex-col items-center justify-center gap-3">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-[#C97B2E] shadow-lg transition-transform duration-300 group-hover:scale-110">
          <Play className="w-7 h-7 text-[#0a0a0a] translate-x-0.5" fill="currentColor" aria-hidden="true" />
        </span>
        <span className="font-body text-xs font-bold uppercase tracking-wider text-white">
          Watch the transformation
        </span>
      </span>
      {/* The accessible name for the whole control. */}
      <span className="sr-only">{`Play video: ${title}`}</span>
    </button>
  );
}
